import { DragEvent, ReactNode, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import {
  getPowerSlideLabel,
  ISlide,
} from "../../../models/interactions/PowerSlideHelper";
import { getPowerSlideTemplate } from "../../../models/interactions/PowerSlideTemplates";
import { PowerSlideTheme } from "../../../models/interactions/PowerSlideTheme";
import PowerSlideCanvas from "./PowerSlideCanvas";
import PowerSlidePopover from "./PowerSlidePopover";
import "./PowerSlideRail.css";

/**
 * Which gap a pointer at `pointerX` lands in, given the cell it is over.
 * Past the cell's midpoint means "after this slide", so the returned value is a
 * gap index in the original list, not a slide index.
 */
export function resolveDropIndex(
  index: number,
  pointerX: number,
  bounds: { left: number; width: number }
): number {
  return pointerX > bounds.left + bounds.width / 2 ? index + 1 : index;
}

/** How close to an edge a dragged slide has to get before the strip creeps. */
const DRAG_SCROLL_ZONE = 72;
/** Pixels per frame at the very edge, tapering to zero at the zone boundary. */
const DRAG_SCROLL_SPEED = 16;

/**
 * Pixels per frame the filmstrip should creep while a slide is held near one of
 * its edges, negative towards the start. Zero anywhere in the middle.
 *
 * A drag can only reach the slides already on screen, so reordering across a
 * deck longer than the panel is otherwise impossible without dropping,
 * scrolling and picking the slide up again.
 *
 * Split out as a pure function because jsdom's drag events carry no clientX:
 * the wiring can be asserted through the DOM, but the ramp itself can only be
 * tested here.
 */
export function resolveDragScrollVelocity(
  pointerX: number,
  bounds: { left: number; right: number },
  zone: number = DRAG_SCROLL_ZONE,
  speed: number = DRAG_SCROLL_SPEED
): number {
  if (bounds.right - bounds.left <= zone * 2) return 0;
  const fromLeft = pointerX - bounds.left;
  const fromRight = bounds.right - pointerX;
  // Ramped by proximity rather than switched on: a constant speed makes the
  // zone boundary feel like a cliff and overshoots on short strips.
  if (fromLeft < zone) return -speed * Math.min(1, (zone - fromLeft) / zone);
  if (fromRight < zone) return speed * Math.min(1, (zone - fromRight) / zone);
  return 0;
}

interface PowerSlideRailProps {
  slides: ISlide[];
  selectedSlideId: string | undefined;
  getValue: (key: string) => string;
  onSelect: (slideId: string) => void;
  onStep: (delta: number) => void;
  onAdd: () => void;
  onDuplicate: (slideId: string) => void;
  onCopy: (slideId: string) => void;
  onDelete: (slideId: string) => void;
  onMove: (slideId: string, toIndex: number) => void;
  theme?: PowerSlideTheme;
}

/** The miniatures are display only, so their write paths go nowhere. */
const noop = () => {};
const emptyKey = () => "";

/** Fraction of the remaining distance covered each frame. */
const SCROLL_EASING = 0.22;
/** Assumed line height for wheels that report lines rather than pixels. */
const SCROLL_LINE_HEIGHT = 16;

const CELL_WIDTH = 186;
const THUMB_WIDTH = 176;
const THUMB_HEIGHT = 99;

/**
 * The Slides panel: a header saying where you are in the deck, and a filmstrip
 * of miniatures.
 *
 * The stepping arrows live here rather than on the canvas. They were dropped
 * from the slide itself in favour of the rail plus the keyboard, but the panel
 * header is still where position is reported and stepped.
 */
export default function PowerSlideRail(props: PowerSlideRailProps) {
  const {
    slides,
    selectedSlideId,
    getValue,
    onSelect,
    onStep,
    onAdd,
    onDuplicate,
    onCopy,
    onDelete,
    onMove,
    theme,
  } = props;
  const { t } = useTranslation();
  const [menuSlideId, setMenuSlideId] = useState<string | undefined>();
  const [draggingId, setDraggingId] = useState<string | undefined>();
  const [dropIndex, setDropIndex] = useState<number | undefined>();
  const thumbRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const menuAnchors = useRef<Record<string, HTMLButtonElement | null>>({});
  const filmstripRef = useRef<HTMLDivElement>(null);
  const scrollTarget = useRef(0);
  /** The eased position, kept in floats because scrollLeft is rounded. */
  const scrollPosition = useRef(0);
  /** What the animation last put in scrollLeft, to spot outside changes. */
  const lastWritten = useRef(0);
  const scrollFrame = useRef<number | undefined>(undefined);
  const dragScrollFrame = useRef<number | undefined>(undefined);
  const dragScrollVelocity = useRef(0);
  const dragScrollPosition = useRef(0);
  const dragPointer = useRef<{ x: number; y: number } | undefined>(undefined);

  /**
   * A wheel over the filmstrip scrolls it sideways. Without this the only
   * horizontal control is the scrollbar itself, which is a poor target for a
   * strip you are meant to skim.
   *
   * Eased towards a target rather than jumping per tick: a mouse wheel arrives
   * in coarse ~120px steps, and applying them raw makes the strip lurch.
   */
  useEffect(() => {
    const element = filmstripRef.current;
    if (!element) return;

    /** Wheels report lines or pages as well as pixels; normalise to pixels. */
    const pixelDelta = (event: WheelEvent) => {
      if (event.deltaMode === 1) return event.deltaY * SCROLL_LINE_HEIGHT;
      if (event.deltaMode === 2) return event.deltaY * element.clientWidth;
      return event.deltaY;
    };

    const stop = () => {
      if (scrollFrame.current !== undefined) {
        cancelAnimationFrame(scrollFrame.current);
      }
      scrollFrame.current = undefined;
    };

    const step = () => {
      const remaining = scrollTarget.current - scrollPosition.current;
      if (Math.abs(remaining) < 0.5) {
        element.scrollLeft = scrollTarget.current;
        scrollPosition.current = element.scrollLeft;
        stop();
        return;
      }
      // The eased position is tracked here rather than read back from the DOM:
      // the browser rounds scrollLeft to whole pixels, so the last fraction of
      // a step would round away to nothing and the loop would never converge.
      scrollPosition.current += remaining * SCROLL_EASING;
      element.scrollLeft = scrollPosition.current;
      lastWritten.current = element.scrollLeft;
      scrollFrame.current = requestAnimationFrame(step);
    };

    /**
     * Hand control back the moment the strip moves by any means other than
     * this animation - dragging the scrollbar, most obviously. Otherwise the
     * loop keeps writing its own target and fights the drag.
     */
    const onScroll = () => {
      if (scrollFrame.current === undefined) return;
      if (Math.abs(element.scrollLeft - lastWritten.current) > 2) stop();
    };

    const onWheel = (event: WheelEvent) => {
      // Trackpads and tilt wheels already send horizontal deltas; only
      // translate a gesture that is genuinely vertical.
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const maxScroll = element.scrollWidth - element.clientWidth;
      if (maxScroll <= 0) return;

      const delta = pixelDelta(event);
      // At either end, let the gesture through so the page still scrolls
      // rather than the strip swallowing it.
      const atStart = element.scrollLeft <= 0 && delta < 0;
      const atEnd = element.scrollLeft >= maxScroll && delta > 0;
      if (atStart || atEnd) return;

      event.preventDefault();
      // Accumulate onto the in-flight target so a fast flick adds up, instead
      // of each tick restarting from wherever the animation happens to be.
      const idle = scrollFrame.current === undefined;
      // Re-seed from the DOM whenever no animation is running, so a wheel after
      // a scrollbar drag continues from where the strip actually sits.
      if (idle) scrollPosition.current = element.scrollLeft;
      const from = idle ? element.scrollLeft : scrollTarget.current;

      scrollTarget.current = Math.max(0, Math.min(maxScroll, from + delta));
      if (idle) {
        lastWritten.current = element.scrollLeft;
        scrollFrame.current = requestAnimationFrame(step);
      }
    };

    // Explicitly not passive: React registers wheel listeners as passive, and
    // the default vertical scroll has to stay cancellable.
    element.addEventListener("wheel", onWheel, { passive: false });
    element.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      element.removeEventListener("wheel", onWheel);
      element.removeEventListener("scroll", onScroll);
      stop();
    };
  }, []);

  // A drag can be abandoned by unmounting the panel - switching interaction,
  // closing the preview - and dragend never arrives, so the creep would run on
  // against a detached element.
  useEffect(
    () => () => {
      if (dragScrollFrame.current !== undefined) {
        cancelAnimationFrame(dragScrollFrame.current);
      }
    },
    []
  );

  const selectedIndex = slides.findIndex(
    (slide) => slide.id === selectedSlideId
  );

  const stopDragScroll = () => {
    if (dragScrollFrame.current !== undefined) {
      cancelAnimationFrame(dragScrollFrame.current);
    }
    dragScrollFrame.current = undefined;
    dragScrollVelocity.current = 0;
    dragPointer.current = undefined;
  };

  const dragScrollStep = () => {
    const element = filmstripRef.current;
    const velocity = dragScrollVelocity.current;
    if (!element || !velocity) {
      stopDragScroll();
      return;
    }
    const maxScroll = element.scrollWidth - element.clientWidth;
    const atEnd =
      (velocity < 0 && element.scrollLeft <= 0) ||
      (velocity > 0 && element.scrollLeft >= maxScroll);
    if (atEnd) {
      stopDragScroll();
      return;
    }
    // Float position for the same reason the wheel animation keeps one: a
    // velocity under a pixel would round away in scrollLeft and never move.
    dragScrollPosition.current = Math.max(
      0,
      Math.min(maxScroll, dragScrollPosition.current + velocity)
    );
    element.scrollLeft = dragScrollPosition.current;
    // dragover only fires on pointer movement, so a pointer held still in the
    // edge zone would keep the drop index from where the drag entered the zone
    // while the strip travels underneath it - and the slide would land there.
    refreshDropIndexUnderPointer();
    dragScrollFrame.current = requestAnimationFrame(dragScrollStep);
  };

  /** Re-reads the drop gap from whatever now sits under the held pointer. */
  const refreshDropIndexUnderPointer = () => {
    const pointer = dragPointer.current;
    if (!pointer) return;
    const cell = document
      .elementFromPoint(pointer.x, pointer.y)
      ?.closest("[data-powerslide-cell]");
    if (!cell) return;
    const index = Number(cell.getAttribute("data-powerslide-cell"));
    if (Number.isNaN(index)) return;
    setDropIndex(
      resolveDropIndex(index, pointer.x, cell.getBoundingClientRect())
    );
  };

  const updateDragScroll = (pointerX: number, pointerY: number) => {
    const element = filmstripRef.current;
    if (!element) return;
    dragPointer.current = { x: pointerX, y: pointerY };
    dragScrollVelocity.current = resolveDragScrollVelocity(
      pointerX,
      element.getBoundingClientRect()
    );
    if (!dragScrollVelocity.current) {
      stopDragScroll();
      return;
    }
    if (dragScrollFrame.current === undefined) {
      // Seed from the DOM: the wheel animation or a scrollbar drag may have
      // moved the strip since this loop last ran.
      dragScrollPosition.current = element.scrollLeft;
      dragScrollFrame.current = requestAnimationFrame(dragScrollStep);
    }
  };

  const endDrag = () => {
    stopDragScroll();
    setDraggingId(undefined);
    setDropIndex(undefined);
  };

  const onDragStart = (event: DragEvent, slide: ISlide) => {
    setDraggingId(slide.id);
    event.dataTransfer.effectAllowed = "move";
    // Drag the thumbnail alone: the label row makes a tilted chip unreadable.
    const thumb = thumbRefs.current[slide.id];
    if (thumb) event.dataTransfer.setDragImage(thumb, THUMB_WIDTH / 2, 50);
  };

  const onDragOverCell = (event: DragEvent, index: number) => {
    if (!draggingId) return;
    event.preventDefault();
    updateDragScroll(event.clientX, event.clientY);
    setDropIndex(
      resolveDropIndex(
        index,
        event.clientX,
        event.currentTarget.getBoundingClientRect()
      )
    );
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    // A cell's drop also bubbles to the rail, which would reorder twice.
    event.stopPropagation();
    if (draggingId && dropIndex !== undefined) onMove(draggingId, dropIndex);
    endDrag();
  };

  const dropIndicator = (index: number) =>
    dropIndex === index ? (
      <div
        aria-hidden="true"
        className="w-[3px] shrink-0 self-stretch rounded-full bg-creator-primary"
      />
    ) : (
      <div aria-hidden="true" className="w-[3px] shrink-0" />
    );

  const stepButton = (
    delta: number,
    label: string,
    icon: ReactNode,
    disabled: boolean
  ) => (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => onStep(delta)}
      className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-solid border-creator-border bg-creator-surface text-creator-text-sub enabled:hover:border-creator-primary enabled:hover:text-creator-primary disabled:opacity-40"
    >
      {icon}
    </button>
  );

  const menu = (slide: ISlide) => (
    // Portalled and preferring upward: the panel sits at the bottom of the pane
    // and the filmstrip is an overflow-x-auto scroller, which clips an in-flow
    // menu entirely.
    <PowerSlidePopover
      anchorRef={{ current: menuAnchors.current[slide.id] ?? null }}
      isOpen
      onClose={() => setMenuSlideId(undefined)}
      role="menu"
      prefer="above"
      ariaLabel={`powerslide-menu-${slide.id}`}
    >
      <div className="w-[168px] py-[4px]">
        <button
          type="button"
          role="menuitem"
          aria-label={`powerslide-duplicate-${slide.id}`}
          onClick={() => {
            onDuplicate(slide.id);
            setMenuSlideId(undefined);
          }}
          className="flex w-full items-center gap-[8px] px-[12px] py-[6px] text-left text-[12px] text-creator-text hover:bg-creator-childHover"
        >
          <LibraryAddOutlinedIcon sx={{ fontSize: 16 }} />
          {t("powerslide.menu.duplicate", { defaultValue: "Duplicate" })}
        </button>
        {/* Duplicate inserts here; Copy targets another deck. Deliberately
            dissimilar icons, since two sheet-like glyphs read as one action. */}
        <button
          type="button"
          role="menuitem"
          aria-label={`powerslide-copy-${slide.id}`}
          onClick={() => {
            onCopy(slide.id);
            setMenuSlideId(undefined);
          }}
          className="flex w-full items-center gap-[8px] px-[12px] py-[6px] text-left text-[12px] text-creator-text hover:bg-creator-childHover"
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
          {t("powerslide.menu.copy", { defaultValue: "Copy" })}
        </button>
        <div className="my-[4px] h-px bg-creator-border" />
        <button
          type="button"
          role="menuitem"
          aria-label={`powerslide-delete-${slide.id}`}
          onClick={() => {
            onDelete(slide.id);
            setMenuSlideId(undefined);
          }}
          className="flex w-full items-center gap-[8px] px-[12px] py-[6px] text-left text-[12px] text-creator-error hover:bg-creator-childHover"
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          {t("powerslide.menu.delete", { defaultValue: "Delete" })}
        </button>
      </div>
    </PowerSlidePopover>
  );

  const cells = slides.flatMap((slide, index) => {
    const template = getPowerSlideTemplate(slide.templateId);
    const templateName = template
      ? t(`powerslide.templates.${template.locId}`, {
          defaultValue: template.name,
        })
      : slide.templateId;
    const raw = getPowerSlideLabel(slide, getValue);
    const isSelected = slide.id === selectedSlideId;
    const isDragging = slide.id === draggingId;

    return [
      dropIndicator(index),
      <div
        key={slide.id}
        className="group relative flex shrink-0 flex-col gap-[8px]"
        // The origin cell fades to read as a hole while its chip is lifted.
        style={{ width: CELL_WIDTH, opacity: isDragging ? 0.3 : 1 }}
        // Lets the auto-scroll loop find which cell is under a stationary
        // pointer as the strip moves beneath it.
        data-powerslide-cell={index}
        draggable
        onDragStart={(event) => onDragStart(event, slide)}
        onDragEnd={endDrag}
        onDragOver={(event) => onDragOverCell(event, index)}
        onDrop={onDrop}
      >
        <button
          type="button"
          aria-label={`powerslide-rail-slide-${index + 1}`}
          aria-current={isSelected}
          onClick={() => onSelect(slide.id)}
          className="flex w-full flex-col gap-[8px] text-left"
        >
          {/* Padding rather than an outside stroke, so selection never shifts
              the cell and never gets clipped by the scroll container. */}
          <div
            ref={(element) => {
              thumbRefs.current[slide.id] = element;
            }}
            className={classNames({
              "w-fit rounded-[11px] border-2 border-solid p-[5px]": true,
              "border-creator-primary": isSelected,
              "border-transparent": !isSelected,
            })}
          >
            {/* The real slide, scaled down and stripped of affordances, rather
                than a second rendering that could drift from the canvas. */}
            <div
              className="pointer-events-none overflow-hidden rounded-[6px] border border-solid border-creator-border"
              style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
            >
              <PowerSlideCanvas
                slide={slide}
                theme={theme}
                readOnly
                getValue={getValue}
                changeValue={noop}
                onUpdateSlide={noop}
                getNewKey={emptyKey}
              />
            </div>
          </div>

          {/* One row - number, title, type chip - so every cell is the same
              height whether or not the slide has been titled. */}
          <div className="flex w-full items-center gap-[6px]">
            <span className="shrink-0 text-[13px] font-medium text-creator-text-sub">
              {index + 1}
            </span>
            <span
              className={classNames({
                "flex-1 truncate text-[13px] font-medium": true,
                "text-creator-primary": isSelected,
                "text-creator-text": !isSelected && !raw.isPlaceholder,
                "text-creator-text-faded": !isSelected && raw.isPlaceholder,
              })}
            >
              {raw.isPlaceholder
                ? t("powerslide.rail.untitled", { defaultValue: "Untitled" })
                : raw.label}
            </span>
            <span className="shrink-0 rounded-[6px] bg-creator-primary-background px-[7px] py-[3px] text-[9.5px] text-creator-text-sub">
              {templateName}
            </span>
          </div>
        </button>

        {/* The top-right corner is empty across all twelve templates. */}
        <button
          type="button"
          ref={(element) => {
            menuAnchors.current[slide.id] = element;
          }}
          aria-label={`powerslide-slide-menu-${index + 1}`}
          aria-expanded={menuSlideId === slide.id}
          onClick={() =>
            setMenuSlideId(menuSlideId === slide.id ? undefined : slide.id)
          }
          className={classNames({
            "absolute right-[10px] top-[10px] rounded-[4px] bg-creator-surface p-[1px] text-creator-text-sub shadow": true,
            "opacity-0 group-hover:opacity-100 focus:opacity-100": true,
            "opacity-100": menuSlideId === slide.id,
          })}
        >
          <MoreHorizIcon sx={{ fontSize: 16 }} />
        </button>

        {menuSlideId === slide.id && menu(slide)}
      </div>,
    ];
  });

  return (
    <div className="flex w-full flex-col border-t border-solid border-creator-border bg-creator-surface pb-[16px] pt-[15px]">
      <div className="flex h-[34px] shrink-0 items-center justify-between px-[24px]">
        <span className="text-[13.5px] font-semibold text-creator-text">
          {t("powerslide.rail.title", { defaultValue: "Slides" })}
        </span>
        <div className="flex items-center gap-[6px]">
          <span
            aria-label="powerslide-rail-position"
            className="text-[13px] font-medium text-creator-text-soft"
          >
            {`${Math.max(selectedIndex, 0) + (slides.length ? 1 : 0)} / ${slides.length}`}
          </span>
          {stepButton(
            -1,
            "powerslide-rail-previous",
            <ChevronLeftIcon sx={{ fontSize: 16 }} />,
            selectedIndex <= 0
          )}
          {stepButton(
            1,
            "powerslide-rail-next",
            <ChevronRightIcon sx={{ fontSize: 16 }} />,
            selectedIndex < 0 || selectedIndex >= slides.length - 1
          )}
        </div>
      </div>

      <div
        ref={filmstripRef}
        className="powerslide-filmstrip mt-[18px] flex w-full items-start gap-[13px] overflow-x-auto px-[24px] pb-[8px]"
        onDragOver={(event) => {
          if (!draggingId) return;
          event.preventDefault();
          // Also here, not only on the cells: the gaps between cells and the
          // strip's own padding are exactly where the edge zones sit, so a
          // pointer held there would otherwise stop the creep.
          updateDragScroll(event.clientX, event.clientY);
        }}
        onDragLeave={stopDragScroll}
        onDrop={onDrop}
      >
        {cells}
        {dropIndicator(slides.length)}
        {/* Trailing add card, the standard pattern in Slides, PowerPoint and
            Canva - and the pager offers the same action above the canvas. */}
        <button
          type="button"
          aria-label="powerslide-rail-add-slide"
          onClick={onAdd}
          className="flex shrink-0 flex-col gap-[8px]"
          style={{ width: CELL_WIDTH }}
        >
          <div className="w-fit rounded-[11px] border-2 border-solid border-transparent p-[5px]">
            <div
              className="flex items-center justify-center rounded-[6px] border-2 border-dashed border-creator-border text-creator-primary"
              style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
            >
              <AddIcon fontSize="small" />
            </div>
          </div>
          <span className="px-[2px] text-[13px] font-medium text-creator-primary">
            {t("powerslide.rail.addSlide", { defaultValue: "Add slide" })}
          </span>
        </button>
      </div>
    </div>
  );
}
